import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

import style from '../../../style';
import type { Color, Size } from '../../../style/types/theme';
import type Theme from '../../../style/types/theme';

export interface CheckBoxProps {
  theme: Theme;
}
export interface CheckBoxInputProps extends CheckBoxProps {
  disabled: boolean;
  $active?: boolean;
}
export interface CheckBoxLabelProps extends CheckBoxInputProps {
  $checked: boolean;
}

const defaultBoxShadow = (color: Color, size: Size) =>
  `0px 1px ${size.units(0.5)} ${color.shadow.primary.default(0.5)}`;

const unchecked = ({ theme: { size, color } }: CheckBoxProps) =>
  keyframes({
    '0%': {
      width: size.units(2),
      transform: `translate(${size.units(2)}, -50%)`,
      background: color.background.accent.default(),
    },
    '50%': {
      width: size.units(4),
      transform: 'translate(0px, -50%)',
      background: `${color.background.primary.default()}`,
    },
    '100%': {
      width: size.units(2),
      transform: 'translate(0px, -50%)',
    },
  });

const checked = ({ theme: { size, color } }: CheckBoxProps) =>
  keyframes({
    '0%': {
      width: size.units(2),
      transform: 'translate(0px, -50%)',
      background: color.background.primary.default(),
    },
    '50%': {
      width: size.units(4),
      transform: 'translate(0px, -50%)',
      background: color.background.accent.default(),
    },
    '100%': {
      width: size.units(2),
      transform: `translate(${size.units(2)}, -50%)`,
    },
  });

export const CheckboxSlider = styled.div<CheckBoxInputProps>(
  ({ theme: { size, color }, disabled }) => ({
    cursor: disabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'none',
    background: disabled
      ? color.background.tertiary.disabled()
      : color.background.tertiary.default(),
    borderRadius: size.units(0.5),

    '&:before': {
      content: "''",
      position: 'absolute',
      width: size.units(2),
      height: size.units(2),
      left: 0,
      top: '50%',
      bottom: 0,
      borderRadius: size.units(2),
      boxShadow: disabled ? undefined : defaultBoxShadow(color, size),
    },
  }),
);

const withAnimation = (Component: ElementType) =>
  styled(Component)<{ $active: boolean }>(
    ({ $active, $hasAnimation }) =>
      css`
        & + ${CheckboxSlider}:before {
          animation: ${$active ? checked : unchecked} ${$hasAnimation ? 0.4 : 0}s
            cubic-bezier(0, 0.4, 0.4, 1);
        }
      `,
  );

export const CheckboxInput = withAnimation(
  styled.input<CheckBoxInputProps>(({ theme: { size, color }, disabled, $active }) => ({
    width: size.units(4),
    height: size.units(4),
    opacity: 0,
    border: 'none',
    backgroundColor: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    appearance: 'none',
    boxSizing: 'border-box',
    padding: 0,

    [`& + ${CheckboxSlider}:before`]: {
      background: disabled
        ? color.background.primary.disabledContrast()
        : color.background.primary.default(),
      transform: 'translate(0, -50%)',
      boxShadow: disabled
        ? undefined
        : `${defaultBoxShadow(color, size)},
        ${color.shadow.accent.default(0.1)} 0px 0px 0px 0rem`,
      transition: 'box-shadow 0.2s cubic-bezier(0, 0.4, 0.4, 1)',
    },

    ...style.mixins.devices.desktop({
      [`&:hover + ${CheckboxSlider}:before`]: {
        boxShadow: disabled
          ? undefined
          : `${defaultBoxShadow(color, size)}, ${color.shadow.accent.default(
              0.1,
            )} 0px 0px 0px ${size.units(1)}
        `,
      },

      [`&:active + ${CheckboxSlider}:before`]: {
        boxShadow: `${defaultBoxShadow(color, size)}, ${color.shadow.accent.default(
          0.1,
        )} 0px 0px 0px 0.25rem;`,
      },
    }),

    ...($active && {
      [`& + ${CheckboxSlider}:before`]: {
        background: disabled
          ? color.background.accent.disabled()
          : color.background.accent.default(),
        transform: `translate(${size.units(2)}, -50%)`,
        boxShadow: `0px 0px 0px rgba(0, 15, 51, 0),
          ${color.shadow.accent.default(0.1)} 0px 0px 0px 0rem`,
        transition:
          'box-shadow 0.2s cubic-bezier(0,0.4,0.4,1), background 0.2s cubic-bezier(0,0.4,0.4,1)',
      },

      ...style.mixins.devices.desktop({
        [`&:hover + ${CheckboxSlider}:before`]: {
          boxShadow: disabled
            ? undefined
            : `0px 0px 0px rgba(0, 15, 51, 0), ${color.shadow.accent.default(
                0.1,
              )} 0px 0px 0px ${size.units(1)}`,
          background: disabled
            ? color.background.accent.disabled()
            : color.background.accent.hover(),
        },

        [`&:active + ${CheckboxSlider}:before`]: {
          boxShadow: `0px 0px 0px rgba(0, 15, 51, 0),
            ${color.shadow.accent.default(0.1)} 0px 0px 0px 0.25rem`,
        },
      }),
    }),
  })),
);

export const CheckBoxLabel = styled.label<CheckBoxProps>(({ theme: { size } }) => ({
  width: size.units(4),
  height: size.units(0.5),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  [CheckboxSlider]: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },

  [CheckboxInput]: {
    margin: 0,
  },
}));

export const CheckboxWrapper = styled.div<CheckBoxProps>(({ theme: { size } }) => ({
  width: 'auto',
  height: size.units(2),
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  [CheckBoxLabel]: {
    position: 'relative',
  },
}));
